import { useNavigate } from 'react-router-dom';
import { Button, DataTable, InfoTip, type DataTableColumn } from '@zarya/ui';
import { t } from '@app/i18n';
import { DRAWER_PARAM, ROUTE_HUMAN_ACCESS } from '@app/navigation';
import {
  conditionsText,
  limitsText,
  type AccessControlState,
  type ViaPolicy,
} from './AccessControlModel';
import s from './AccessControl.module.css';

/**
 * Aba **Via Human Access** (FR-075, FR-076): o que as regras de Human Access
 * ativas **citam** para esta identidade.
 *
 * Somente leitura — quem edita é a tela de Human Access (T11).
 *
 * A REGRA SÓ RESTRINGE, NUNCA ALARGA (04/09)
 *
 *   Este texto dizia «o teto efetivo é o **maior** entre contrato e política» —
 *   semântica de união (FR-075), que contradizia a interseção dos dois eixos e
 *   ficou registrada como pendência enquanto a Fase 1 era só rename. O
 *   `usecases/access` foi invertido: **efetivo = o mais restritivo**, a regra só
 *   aperta, e capability citada fora do contrato não tem efeito nenhum. Agora o
 *   texto conta a mesma história que o cálculo.
 *
 *   Só o texto mudou aqui: o valor de `effective_ceiling` já vem certo do
 *   servidor, e `ceiling_by_policy` passou a significar «foi a regra que
 *   apertou», não «foi a política que levantou».
 */
export function ViaPoliciesTab({ estado }: { estado: AccessControlState }) {
  const navigate = useNavigate();
  const nome = estado.identity.slug ?? t('identityAccess.identityFallback');

  const columns: DataTableColumn<ViaPolicy>[] = [
    {
      key: 'capability',
      header: t('identityAccess.viaPolicies.col.capability'),
      cell: (linha) => <span className={s.capability}>{linha.capability}</span>,
    },
    {
      key: 'limite',
      header: t('identityAccess.viaPolicies.col.limit'),
      cell: (linha) => {
        const effective = limitsText(linha.effective_ceiling);
        return (
          <span className={s.tool}>
            <b>{limitsText(linha.constraints)}</b>
            <span className={s.muted}>
              {t('identityAccess.viaPolicies.effective', { value: effective })}{' '}
              {linha.ceiling_by_policy
                ? t('identityAccess.viaPolicies.restrictedBy', { policy: linha.policy_name })
                : t('identityAccess.viaPolicies.fromContract')}
            </span>
          </span>
        );
      },
    },
    {
      key: 'politica',
      header: t('identityAccess.viaPolicies.col.rule'),
      cell: (linha) => linha.policy_name,
    },
    {
      key: 'condicoes',
      header: t('identityAccess.viaPolicies.col.conditions'),
      cell: (linha) => <span className={s.muted}>{conditionsText(linha.conditions)}</span>,
    },
    {
      key: 'acao',
      header: '',
      numeric: true,
      cell: (linha) => (
        <Button
          variant="out"
          size="sm"
          // `?drawer=`, não `?politica=`. T11 lê o drawer aberto de `?drawer=<id>`
          // (`useDrawerParam`), e o parâmetro errado levava à lista com a regra
          // fechada — o «abrir» abria a tela, não a regra que a linha aponta.
          // Defeito antigo, encontrado ao trocar a rota em 03/09.
          onClick={() => navigate(`${ROUTE_HUMAN_ACCESS}?${DRAWER_PARAM}=${linha.policy_id}`)}
        >
          {t('identityAccess.viaPolicies.open')}
        </Button>
      ),
    },
  ];

  return (
    <DataTable
      label={t('identityAccess.viaPolicies.label', { name: nome })}
      columns={columns}
      rows={estado.via_policies}
      rowKey={(linha) => `${linha.policy_id}-${linha.capability}`}
      filters={
        <span className={s.nota}>
          {t('identityAccess.viaPolicies.note')}
          <InfoTip
            text={t('identityAccess.viaPolicies.tip')}
            label={t('identityAccess.viaPolicies.tipLabel')}
            size="xs"
          />
        </span>
      }
      emptyIcon="shield"
      emptyTitle={t('identityAccess.viaPolicies.empty.title', { name: nome })}
      emptyMessage={t('identityAccess.viaPolicies.empty.message')}
    />
  );
}
